import { useSmartVisibleCrossSellTracker } from "@packages/polling-checker"
import { Card } from "@shopify/polaris"
import { observer } from "mobx-react"
import qs from "qs"
import { Fragment } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { ShipmentsAndLookupData } from "@/api/dashboard/type"
import { ToProtection } from "@/component/Protection"
import { CROSS_SELL_POINT_ENUM } from "@/enums/CrossSellPoint"
import OrdersNav from "@/enums/orders-nav"
import { ShipmentStatus } from "@/models/IShipmentsLookupsData"
import stores from "@/stores"

import OrderStatusCard from "./OrderStatusCard"

interface OrderStatusListProps {
  loading?: boolean
  dataset: ShipmentStatus[]
  orderTime: {
    selected?: string
    orderedAtMin?: string
    orderedAtMax?: string
  }
  chartData: ShipmentsAndLookupData["shipments_lookups"]["shipment_status_chart_data"]
}

const OrderStatusList = observer(function OrderStatusList({
  loading,
  dataset,
  orderTime,
  chartData,
}: OrderStatusListProps){
  const { t } = useTranslation("dashboard", { keyPrefix: "OrderStatusBar" })
  const navigate = useNavigate()
  const status = new Map<number, number>()
  const { selected, orderedAtMin, orderedAtMax } = orderTime
  const { exception, failed_attempt, pending } = chartData

  // Dashboard异常保护推荐的可见性监听
  const { ref: dashboardProtectionRef } = useSmartVisibleCrossSellTracker({
    onViewed: () => {
      window.CrossSellTracker?.logViewed(CROSS_SELL_POINT_ENUM.DashboardExceptionCard)
    },
    enabled: stores.userInfoSore.data.is_pi_eligible && !stores.userInfoSore.data.is_pi_active && stores.userInfoSore.data.has_exception_shipment,
    threshold: 0.3, // 30% 可见时触发
  })

  dataset.forEach(item => status.set(item.type, item.value))

  const handleClick = (status:number) => {
    if (!loading) {
      navigate({
        pathname: "/orders",
        search: qs.stringify({
          orderedAt: selected && selected !== "0" ? "1" === selected ? "Today" : `Last ${stores.EventsStore.selected} days` : void 0,
          orderedAtMin,
          orderedAtMax,
          status: OrdersNav[status],
        }, {
          skipNulls: true,
        }),
      })
    }
  }

  const OrderStatusList = [
    {
      title: t("exception"),
      description: t("exceptionDesc"),
      type: 7,
      value: status.get(7),
      onClick: handleClick,
      chartData: exception,
      extra: (
        <div ref={dashboardProtectionRef as any}>
          <ToProtection pointType="exception_card" CrossSellTrackerPoint={CROSS_SELL_POINT_ENUM.DashboardExceptionCard}  needCheckShipment />
        </div>
      ),
    },
    { title: t("failedAttempt"),
      description: t("failedAttemptDesc"),
      type: 6,
      value: status.get(6),
      onClick: handleClick,
      chartData: failed_attempt,
    },
    { title: t("pending"),
      description: t("pendingDesc"),
      type: 1,
      value: status.get(1),
      onClick: handleClick,
      chartData: pending,
    },
  ]

  return <Card padding={"300"}>
    <div className={"flex flex-wrap gap-3 flex-col md:flex-row"}>
      {
        OrderStatusList.map((status, index) => <Fragment key={status.type}>
          <OrderStatusCard {...status} />
          {index < OrderStatusList.length - 1
            && <div className={"w-full h-[1px] md:w-[1px] md:h-[66px] bg-[var(--p-color-border-secondary)]"} />
          }
        </Fragment>)
      }
    </div>
  </Card>
})

export default OrderStatusList
